<template>
    <ValidationObserver tag="form" ref="observer" novalidate @submit.prevent="onSubmit">
        <!-- Native constraint validation (min/max on datetime-local inputs)
             is disabled on purpose: vee-validate owns ALL validation here.
             Without novalidate, editing an event whose start is in the past
             trips the native min="now" and the browser silently swallows the
             submit (no PATCH, no vee error, no toast). -->
        <!-- SGA/LFP pickers render inside the "Información básica" Card
             (EventoBasicInfo) in a responsive grid; this form keeps the
             SGA state handlers and re-receives picker events from there. -->

        <div :class="['tw:grid tw:grid-cols-1 xl:tw:grid-cols-2 tw:gap-6', props.loading ? 'tw:pointer-events-none' : '']">
            <!-- Error section -->
            <div v-if="listaErrores?.length" ref="erroresBackend" class="xl:tw:col-span-2">
                <div class="tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4">
                    <ul class="tw:pl-4 tw:mb-0 tw:space-y-1">
                        <li
                            v-for="(error, i) in listaErrores"
                            :key="i"
                            class="tw:text-sm tw:text-red-700"
                        >
                            {{ error }}
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Lookup failure banner. Decisión explícita: un lookup fallido NO
                 bloquea el formulario (son ayudas para los selects; bloquear todo
                 el alta por un club que no cargó sería peor), pero deja de ser
                 silencioso — antes los selects quedaban vacíos sin distinguir
                 "cargando", "falló" o "no hay datos". Se ofrece reintento porque
                 un fallo transitorio de red no debería obligar a recargar la página. -->
            <div v-if="lookupsHasErrors && !lookupsLoading" class="xl:tw:col-span-2">
                <div class="tw:bg-amber-50 tw:border tw:border-amber-200 tw:rounded-lg tw:p-3 tw:flex tw:items-center tw:justify-between tw:gap-4">
                    <p class="tw:text-sm tw:text-amber-800 tw:mb-0">
                        {{ 'Algunos desplegables no se han podido cargar. Puedes continuar, pero revisa que los campos dependientes estén completos.' }}
                    </p>
                    <button
                        type="button"
                        data-cy="reintentar-lookups"
                        class="tw:shrink-0 tw:text-sm tw:font-medium tw:text-amber-900 tw:underline tw:bg-transparent tw:border-0"
                        @click="retryLookups"
                    >
                        {{ 'Reintentar' }}
                    </button>
                </div>
            </div>

            <!-- Card Column 1 -->
            <div class="tw:space-y-6">
                <EventoBasicInfo
                    :clubes="clubes"
                    :divisiones="divisiones"
                    :estados="estados"
                    :tags="tags"
                    :manager="manager"
                    @lfp-sga-selected="onLfpSgaSelected"
                    @lfp-sga-deselected="onLfpSgaDeselected"
                    @sga-product-selected="onSgaProductSelected"
                    @sga-product-deselected="onSgaProductDeselected"
                    @sga-no-products="onSgaNoProducts"
                />

                <EventoLocation
                    :recintos="recintos"
                    @set-recinto="setRecinto"
                    @change-address="changeAddress"
                />

                <EventoScheduling
                    :url="uploadUrl"
                    @imagen-subida="imagenSubida"
                    @imagen-borrada="imagenBorrada"
                    @clear-fecha="clearFecha"
                />

                <EventoTextEditors ref="textEditors" />

                <EventoTicketing
                    :puedeConfigurarCesionTemporal="puedeConfigurarCesionTemporal"
                    :cesionTemporalBloqueadaPor="cesionTemporalBloqueadaPor"
                    :cesionTemporalMensajeTooltip="cesionTemporalMensajeTooltip"
                />
            </div>

            <!-- Card Column 2 -->
            <div class="tw:space-y-6">
                <EventoAbonados
                    :tipos="tipos"
                    :descuentos="descuentos"
                />

                <EventoSales
                    :limitesTiempo="limitesTiempo"
                    :manager="manager"
                    :suppress-nominativas-scroll="suppressNominativasScroll"
                />

                <EventoPreventa
                    ref="preventaEditors"
                    :maxPreventaDate="maxPreventaDate"
                />

                <EventoConfig :manager="manager" />

                <EventoSharedTickets
                    :eventos="eventos"
                    :eventos-loading="eventosLoading"
                />

            </div>
            <!-- Submit flotante: fixed al fondo de la pantalla, padding-bottom
            en el grid para que el contenido no quede tapado. -->
        </div>

        <!-- Capa exterior: full-width con bg + blur (cubre de borde a borde del viewport).
             Capa interior: max-w-7xl centrada, contiene el botón respetando el ancho del form. -->
        <div class="tw:sticky tw:bottom-0 tw:-mx-5 tw:md:-mx-10 tw:w-[calc(100%+2.5em)] tw:md:w-[calc(100%+5.2em)] tw:bg-gray-100/50 tw:backdrop-blur-lg tw:z-40">
            <div class="tw:px-5 tw:md:px-10 tw:py-6 tw:mt-4 tw:-mb-2">
                <div class="tw:mx-auto">
                    <Button
                        type="submit"
                        id="guardar-evento"
                        data-cy="guardar-evento"
                        :disabled="props.loading"
                        variant="success"
                        custom-class="tw:w-full tw:py-3 tw:text-base tw:rounded-md"
                    >
                        <span v-if="props.loading">{{ 'Guardando...' }}</span>
                        <span v-else>{{ 'GUARDAR' }}</span>
                    </Button>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, computed, onMounted, provide, watch, nextTick } from 'vue';
    import { useEventForm } from '@/composables/admin/eventos/useEventForm';
    import { config as appConfig } from '@/config';
    import EventoBasicInfo from './EventoBasicInfo.vue';
    import EventoLocation from './EventoLocation.vue';
    import EventoScheduling from './EventoScheduling.vue';
    import EventoTextEditors from './EventoTextEditors.vue';
    import EventoTicketing from './EventoTicketing.vue';
    import EventoAbonados from './EventoAbonados.vue';
    import EventoSales from './EventoSales.vue';
    import EventoPreventa from './EventoPreventa.vue';
    import EventoConfig from './EventoConfig.vue';
    import EventoSharedTickets from './EventoSharedTickets.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import { useToast } from '@/composables/common/useToast';

    const props = defineProps({
        evento: { type: Object, required: true },
        errorsPeticion: { type: Object, default: () => ({}) },
        // When true, applies opacity-50 + pointer-events-none to the form
        // grid so the user sees the form stays in place during submit (no
        // blank page flash) while every input is effectively disabled.
        // The submit button keeps its own :disabled="loading" state so the
        // visual disabled matches the functional disabled.
        loading: { type: Boolean, default: false }
    });

    const emit = defineEmits(['terminado']);

    const observer = ref(null);
    const textEditors = ref(null);
    const preventaEditors = ref(null);
    const erroresBackend = ref(null);
    const toast = useToast();

    const {
        localData,
        clubes, divisiones, estados, recintos, tags, eventos, eventosLoading, limitesTiempo, tipos,
        configuracion, manager, teatro, recinto, lista,
        maxPreventaDate, listaErrores, shouldHideSgaPickers, sportsFieldsFilled, imageRequired, errorEvo,
        cesionTemporalBloqueadaPor, cesionTemporalMensajeTooltip, puedeConfigurarCesionTemporal, descuentos,
        submit, changeAddress, setRecinto, clearFecha, imagenSubida, imagenBorrada,
        setSgaData, getEventos,
        markMetaTitleAsUserModified,
        lookupsLoading, lookupsHasErrors, loadAll,
    } = useEventForm(props, { emit });

    // Reintento de los lookups fallidos. `loadAll()` es idempotente y vuelve a
    // pedir sólo lo que el formulario necesita; un fallo transitorio de red deja
    // de obligar a recargar la página entera.
    function retryLookups() {
        return loadAll({ listaAbonadosId: props.evento?.lista_abonados_id });
    }

    // When backend returns validation errors, scroll the banner into view so
    // the user lands on them instead of the still-empty submit button.
    watch(listaErrores, nuevos => {
        if (nuevos?.length) {
            nextTick(() => {
                erroresBackend.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }
    });

    // Provide reactive localData to child cards so they can mutate it
    // directly without violating vue/no-mutating-props (canonical pattern
    // mirrored from src/modules/admin/bases/Index.vue + provide('formData')).
    provide('eventoFormData', localData);

    // Provide the markMetaTitleAsUserModified toggle so EventoScheduling
    // can flip it when the user types into the meta_title input. The flag
    // gates the auto-mirror watcher in useEventFormWatchers.js (which
    // copies `name + author` into `localData.meta_title`); once the user
    // has manually edited meta_title, the watcher stops overwriting it.
    // meta_description has no auto-fill, so no toggle for it.
    provide('markMetaTitleAsUserModified', markMetaTitleAsUserModified);

    // Shared cross-field flags for the child cards. `sportsFieldsFilled` is
    // true as soon as any of equipo_local / equipo_visitante / competición is
    // set: EventoBasicInfo uses it to make the trio mutually required, and
    // EventoScheduling uses it to relax the image requirement — same predicate,
    // one definition in useEventForm.
    //
    // `errorEvo` gates the submit for AvanzaEsp (`isValid && !errorEvo`, como el
    // formulario legacy). El estado visual del select lo pinta el propio
    // BaseMultiselect vía la rule `evoPartidoRequerido`, no un mensaje aparte.
    //
    // `imageRequired` decides whether EventoScheduling demands an image: it is
    // mandatory only for non-draft events (published/cancelled/postponed) and
    // exempts the sports trio, same predicate the composable already exposes.
    provide('eventoFormValidation', { sportsFieldsFilled, errorEvo, imageRequired });

    // Estado de carga de los lookups, para que los selects que dependen de ellos
    // (equipos, competición, estado) se deshabiliten mientras llegan los datos y
    // no muestren opciones vacías como si no hubiera ninguna. La regla del
    // repositorio exige loader además del estado de error.
    provide('eventoFormLookups', { lookupsLoading });

    const uploadUrl = computed(() => {
        const token = localStorage.getItem('token') || '';
        return `${appConfig.API_BASE}/upload/image?api_key=${token}&permisos=public`;
    });

    // Build editorRefs map from sub-component exposed refs
    const editorRefs = computed(() => ({
        editorSynopsis: textEditors.value?.editorSynopsis,
        editorCondicionesAcreditaciones: textEditors.value?.editorCondicionesAcreditaciones,
        editorUnderagePolicy: textEditors.value?.editorUnderagePolicy,
        editorReglamento: textEditors.value?.editorReglamento,
        editorRegistroPreventa: preventaEditors.value?.editorRegistroPreventa,
        editorCondicionesLegales: preventaEditors.value?.editorCondicionesLegales,
    }));

    // Passed to EventoSales: when true, activating "Entradas nominativas"
    // does NOT scroll to its section. Raised for exactly one watcher tick by
    // onLfpSgaSelected (programmatic activation), so only the SGA selection
    // suppresses the scroll — user clicks on the toggle keep it.
    const suppressNominativasScroll = ref(false);

    function onLfpSgaSelected(selectedOption) {
        // Experiencia and SGA/LFP event are mutually exclusive. If the user
        // picks an event while Experiencia is checked, un-check it (and its
        // dependent state) first — an event cannot be both an experience and
        // an SGA/LFP-bound event. (The inverse path — checking Experiencia
        // clears the event — lives in useEventFormWatchers.)
        if (localData.experiencia) {
            localData.experiencia = false;
        }
        if (teatro.value?.sga) {
            localData.sga_id = selectedOption.id;
            setSgaData(selectedOption);
        } else {
            localData.lfp_id = selectedOption.id;
        }
        // Programmatic nominativas activation: suppress the scroll-to-section
        // for exactly one watcher tick (the watcher reads this prop when it
        // fires; nextTick guarantees the watcher has already run and skipped).
        suppressNominativasScroll.value = true;
        localData.solicitar_datos = true;
        nextTick(() => {
            suppressNominativasScroll.value = false;
        });
    }

    // Deselect on the event picker: clear sga_id/lfp_id AND every dependent
    // state — the product picker unmounts (v-if on sga_id), the auto-activated
    // nominativas revert, and the mandatory-products gate resets.
    function onLfpSgaDeselected() {
        if (teatro.value?.sga) {
            localData.sga_id = null;
            localData.sga_product_id = null;
            sgaNoProducts.value = false;
        } else {
            localData.lfp_id = null;
        }
        localData.solicitar_datos = false;
    }

    function onSgaProductSelected(selectedOption) {
        localData.sga_product_id = selectedOption.id;
        // A product exists now (picked manually or auto-selected) → the
        // mandatory-products gate reopens if a previous SGA event had none.
        sgaNoProducts.value = false;
    }

    function onSgaProductDeselected() {
        localData.sga_product_id = null;
    }

    // Tracks the SgaProductPicker's empty state. When the selected SGA event
    // has NO associated products (or the products fetch failed), the event
    // cannot be created: products are mandatory. `onSubmit` checks this flag
    // before delegating to the composable's submit and surfaces a toast.
    const sgaNoProducts = ref(false);

    function onSgaNoProducts() {
        sgaNoProducts.value = true;
    }

    // Wraps the composable's submit so we can react to validation failures by
    // scrolling the first invalid field into view (the composable returns
    // false when it blocks emission due to client-side validation errors).
    async function onSubmit() {
        // Mandatory-products gate: SGA events must have a product associated
        // before the event can be created/updated. Checked here (before the
        // composable's validation+submit) so the user gets an explicit
        // message instead of a silent backend failure.
        if (sgaNoProducts.value) {
            toast.error('Este evento SGA no tiene productos asociados. Los productos son obligatorios para crear el evento.');
            return;
        }

        // AvanzaEsp: el vínculo con el partido es obligatorio para teatros con
        // evo_enabled (vincular el partido, o marcar "crear partido" al crear).
        // Se evalúa JUNTO al resto de la validación — `submit` sólo emite si
        // `isValid && !errorEvo`, igual que el formulario legacy — y el select
        // pinta su propio error vía la rule `evoPartidoRequerido`, así que
        // `observer.validate()` deja todos los campos en rojo a la vez y el
        // scroll aterriza en el primero en orden de DOM.
        const ok = await submit(observer.value, editorRefs.value, { extraValid: !errorEvo.value });

        if (!ok) {
            await nextTick();
            scrollToFirstError();
        }
    }

    function scrollToFirstError() {
        // Anchor: any base component with an active ValidationProvider error
        // sets data-validation-error="" on its wrapper. We pick the first
        // one in DOM order (top-down), which is the canonical pattern
        // mirrored across BaseInputText / BaseMultiselect / BaseTextarea /
        // BaseDateTimeInput / BaseDateInput.
        const el = document.querySelector('[data-validation-error=""]');
        if (el && typeof el.getBoundingClientRect === 'function') {
            // block: 'center' con offset 100px hacia el top del viewport
            // (mismo offset que useScrollOnToggle) → el elemento queda
            // centrado verticalmente pero 100px por encima del centro.
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight;
            const targetY = window.scrollY + rect.top - (vh - rect.height) / 2 + 100;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
        }
    }

    onMounted(() => {
        observer.value?.reset();
    });
</script>
